import type { AuthUser, AuthUserState, GyymApiClient } from '@gyym/sdk'
import { getAuthUserState } from '@gyym/sdk'
import type { GetServerSidePropsContext } from 'next'

import { createServerGyymApiClient } from '#utils/createServerGyymApiClient'
import { gyymPrefetch } from '#utils/gyymPrefetch'
import { ServerCachedContext } from '#utils/types'

export const withGyymAuthRedirect = <P extends { initialUser?: AuthUser }>(
  destination: string,
  condition: (
    context: GetServerSidePropsContext,
    auth: { authUser?: AuthUser; authState: AuthUserState },
  ) => boolean | Promise<boolean>,
  permanent = false,
) => {
  return async (
    ctx: GetServerSidePropsContext,
    { composedProps, client: initialClient }: ServerCachedContext<P>,
  ) => {
    const client: GyymApiClient =
      initialClient ??
      createServerGyymApiClient({
        ctx,
      })

    try {
      const [, authUser] = composedProps?.initialUser
        ? ['', composedProps?.initialUser]
        : await gyymPrefetch(client.auth.user.get)

      // Redirect
      if (
        await condition(ctx, {
          authUser,
          authState: getAuthUserState(authUser),
        })
      ) {
        return {
          redirect: {
            destination,
            permanent,
          },
        }
      }

      return {
        props: {},
      }
    } catch (e) {
      if (client.isHttpError(e) && e.status === 404) {
        return {
          notFound: true,
        } as const
      }

      console.error(e)

      throw e
    }
  }
}
